import React from 'react'
import { useDispatch } from 'react-redux'
import { Box, Avatar, IconButton, Typography, Paper, Grid, useTheme } from '@mui/material'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import CloseIcon from '@mui/icons-material/Close'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import { UploadPhotoButton } from 'pages/Users/Profile/Buttons'
import { hostAddress } from 'data/helpers'
import { fetchUpdateCandidate } from 'src/redux/candidate'
import ResumeUpload from 'components/UserFields/ResumeUpload'
import { getPhysicianProfileImage } from 'src/pages/Physicians/helpers'

export const FileSection = ({ formData, candidate }) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const imageURL = getPhysicianProfileImage(candidate.profileImage)

  const handleResumeClick = () => {
    fetch(
      hostAddress(
        `/api/storage/resume?key=${formData.resume.key}&fileName=${formData.resume.fileName}`,
      ),
      { method: 'GET' },
    ).then(response => response.blob().then(blob => window.open(URL.createObjectURL(blob))))
  }

  const handleRemoveResume = () => {
    dispatch(fetchUpdateCandidate({ id: candidate.id, resumeFile: null }))
  }

  const handleResumeSave = event => {
    const file = event.target.files?.[0]
    if (file) {
      dispatch(fetchUpdateCandidate({ id: candidate.id, resumeFile: file }, 'Uploaded Resume'))
    }
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Profile Image Column */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: 360,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              background: theme.palette.background.paper,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5
            }}
          >
            <Box sx={{ mb: 2.5 }}>
              {imageURL ? (
                <Avatar 
                  src={imageURL} 
                  sx={{ 
                    width: 120, 
                    height: 120,
                    border: '2px solid',
                    borderColor: 'background.paper',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  }} 
                />
              ) : (
                <Avatar 
                  sx={{ 
                    width: 120, 
                    height: 120,
                    bgcolor: theme.palette.primary.main,
                    border: '2px solid',
                    borderColor: 'background.paper',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  }}
                >
                  <AccountCircleIcon sx={{ fontSize: 80 }} />
                </Avatar>
              )}
            </Box>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                mb: 2,
                fontWeight: 500,
                color: theme.palette.text.primary
              }}
            >
              Profile Photo
            </Typography>
            <Box sx={{ mt: 'auto' }}>
              <UploadPhotoButton 
                buttonId='profile-upload' 
                user={{ ...candidate, type: 'candidate' }}
                sx={{
                  '& .MuiButton-root': {
                    borderRadius: 1,
                    textTransform: 'none',
                    px: 2.5,
                    py: 1,
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }
                }}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Resume Column */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: 360,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              background: theme.palette.background.paper,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5
            }}
          >
            <Box sx={{ mb: 2.5 }}>
              <Avatar 
                sx={{ 
                  width: 120, 
                  height: 120,
                  bgcolor: theme.palette.primary.main,
                  border: '2px solid',
                  borderColor: 'background.paper',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              >
                <InsertDriveFileIcon sx={{ fontSize: 60 }} />
              </Avatar>
            </Box>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                mb: 2,
                fontWeight: 500,
                color: theme.palette.text.primary
              }}
            >
              Resume
            </Typography>
            {formData.resume ? (
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  mb: 2,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider',
                  width: '100%',
                  maxWidth: 320
                }}
              >
                <Typography
                  component='a'
                  onClick={handleResumeClick}
                  sx={{
                    textDecoration: 'none',
                    cursor: 'pointer',
                    color: theme.palette.primary.main,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    mr: 1.5,
                    flex: 1,
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {formData.resume.fileName}
                </Typography>
                <IconButton 
                  onClick={handleRemoveResume} 
                  color='error' 
                  size='small'
                  sx={{
                    p: 0.5,
                    '&:hover': {
                      bgcolor: 'error.lighter'
                    }
                  }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            ) : (
              <Typography 
                variant='body2' 
                color='text.secondary' 
                sx={{ 
                  mb: 2, 
                  textAlign: 'center',
                  fontSize: '0.875rem'
                }}
              >
                No resume uploaded
              </Typography>
            )}
            <Box sx={{ mt: 'auto' }}>
              <ResumeUpload
                id='resume-upload'
                value={formData.resume}
                handleChange={handleResumeSave}
                label={formData.resume ? 'Update Resume' : 'Upload Resume'}
                name='resume'
                loading={candidate?.loading}
                sx={{
                  '& .MuiButton-root': {
                    borderRadius: 1,
                    textTransform: 'none',
                    px: 2.5,
                    py: 1,
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }
                }}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

export default FileSection
